import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import * as defaultApi from "../api/coachApi";
import { classifyMessageError, validationCopy } from "../domain/coachMessageOutcome";
import { clearOutbox, createClientMessageId, readOutbox, writeOutbox } from "../domain/coachOutbox";
import {
  buildThreadItems,
  createThreadState,
  findUserEvent,
  isConversationBusy,
  retryTextFor,
  threadReducer,
  TURN_PHASES,
} from "../domain/coachThread";

/*
 * The /ai conversation: latest page of events, older pages on demand, and the
 * athlete's messages with idempotent delivery (contract §5.3, api-s2 §4).
 *
 *   - one clientMessageId per typed message, reused by every retry of it, and
 *     kept in the tab's outbox so a reload can retry it too;
 *   - one message at a time: send() refuses while a reply is pending;
 *   - a reply still being produced (COACH_MESSAGE_IN_PROGRESS, or
 *     `pendingReply` after a reload) is awaited by re-reading /events until it
 *     is DONE or FAILED, or the server lease (45 s) has long passed.
 *
 * Callbacks report what the rest of the screen must reflect: new limits, an
 * exhausted budget, a stale /home (consent or availability changed), saved
 * memories.
 */

const PAGE_SIZE = 30;
const POLL_INTERVAL_MS = 3000;
const REPLY_WAIT_MS = 50_000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function useCoachConversation({
  enabled,
  userId,
  pendingReply = null,
  onLimits,
  onBudgetExhausted,
  onHomeStale,
  onMemoriesChanged,
  api = defaultApi,
  pollIntervalMs = POLL_INTERVAL_MS,
  replyWaitMs = REPLY_WAIT_MS,
}) {
  const [state, dispatch] = useReducer(threadReducer, undefined, createThreadState);
  const [loadStatus, setLoadStatus] = useState("idle");
  const [loadingOlder, setLoadingOlder] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const aliveRef = useRef(true);
  const sendingRef = useRef(false);
  const waitingRef = useRef(new Set());
  const callbacksRef = useRef({});
  callbacksRef.current = { onLimits, onBudgetExhausted, onHomeStale, onMemoriesChanged };
  const pendingReplyRef = useRef(pendingReply);
  pendingReplyRef.current = pendingReply;

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const waitForReply = useCallback(async (clientMessageId) => {
    if (waitingRef.current.has(clientMessageId)) return;
    waitingRef.current.add(clientMessageId);
    const deadline = Date.now() + replyWaitMs;
    try {
      while (aliveRef.current) {
        await sleep(pollIntervalMs);
        if (!aliveRef.current) return;
        let page;
        try {
          page = await api.getCoachEvents({ limit: PAGE_SIZE });
        } catch {
          page = null;
        }
        if (!aliveRef.current) return;
        if (page) {
          dispatch({ type: "EVENTS_LOADED", mode: "latest", events: page.events, nextBefore: page.nextBefore });
          const event = page.events.find(
            (item) => item.type === "MESSAGE_USER" && item.clientMessageId === clientMessageId
          );
          if (event?.reply?.status === "DONE") {
            dispatch({ type: "TURN_REMOVED", clientMessageId });
            clearOutbox(userId, clientMessageId);
            return;
          }
          if (event?.reply?.status === "FAILED") {
            dispatch({
              type: "TURN_UPDATED",
              clientMessageId,
              patch: { phase: TURN_PHASES.FAILED, retryable: true, failureCode: event.reply.failureCode || null },
            });
            return;
          }
          const otherPending = page.events.some(
            (item) => item.type === "MESSAGE_USER" && item.reply?.status === "PENDING"
          );
          if (!event && !otherPending) {
            // The pending reply was another message's; this one was never stored.
            dispatch({ type: "TURN_UPDATED", clientMessageId, patch: { phase: TURN_PHASES.UNSENT, retryable: true, failureCode: null } });
            return;
          }
        }
        if (Date.now() >= deadline) {
          dispatch({
            type: "TURN_UPDATED",
            clientMessageId,
            patch: { phase: TURN_PHASES.FAILED, retryable: true, failureCode: "COACH_TIMEOUT" },
          });
          return;
        }
      }
    } finally {
      waitingRef.current.delete(clientMessageId);
    }
  }, [api, pollIntervalMs, replyWaitMs, userId]);

  const deliver = useCallback(async (clientMessageId, text, { allowRegenerate = true } = {}) => {
    dispatch({ type: "TURN_UPDATED", clientMessageId, patch: { phase: TURN_PHASES.SENDING, failureCode: null } });
    try {
      const response = await api.postCoachMessage({ text, clientMessageId });
      if (!aliveRef.current) return { status: "sent" };
      dispatch({ type: "EVENTS_ADDED", events: [response.userEvent, response.coachEvent].filter(Boolean) });
      dispatch({ type: "TURN_REMOVED", clientMessageId });
      clearOutbox(userId, clientMessageId);
      callbacksRef.current.onLimits?.(response.limits);
      const memory = response.coachEvent?.memory;
      if (!response.replayed && (memory?.savedIds?.length || memory?.updatedIds?.length)) {
        callbacksRef.current.onMemoriesChanged?.(memory);
      }
      return { status: "sent" };
    } catch (error) {
      if (!aliveRef.current) return { status: "failed" };
      const outcome = classifyMessageError(error);
      if (outcome.kind === "regenerate" && allowRegenerate) {
        dispatch({ type: "TURN_REMOVED", clientMessageId });
        const nextId = createClientMessageId();
        const startedAt = new Date().toISOString();
        dispatch({ type: "TURN_STARTED", clientMessageId: nextId, text, at: startedAt });
        writeOutbox(userId, { clientMessageId: nextId, text, startedAt });
        return deliver(nextId, text, { allowRegenerate: false });
      }
      if (outcome.kind !== "turn") {
        const storedOnServer = Boolean(findUserEvent(stateRef.current, clientMessageId));
        dispatch({ type: "TURN_REMOVED", clientMessageId });
        clearOutbox(userId, clientMessageId);
        if (outcome.reason === "LIMIT") callbacksRef.current.onLimits?.(outcome.limits);
        if (outcome.reason === "BUDGET") callbacksRef.current.onBudgetExhausted?.();
        if (outcome.reason === "HOME_STALE") callbacksRef.current.onHomeStale?.();
        return {
          status: "rejected",
          reason: outcome.reason,
          // A message the server already holds keeps its own bubble and Retry.
          restoreText: storedOnServer ? null : text,
          message: outcome.reason === "VALIDATION" ? validationCopy(outcome.fields) : null,
        };
      }
      dispatch({
        type: "TURN_UPDATED",
        clientMessageId,
        patch: { phase: outcome.phase, retryable: outcome.retryable, failureCode: outcome.failureCode },
      });
      if (outcome.phase === TURN_PHASES.EXHAUSTED) clearOutbox(userId, clientMessageId);
      if (outcome.phase === TURN_PHASES.WAITING) waitForReply(clientMessageId);
      return { status: outcome.phase };
    }
  }, [api, userId, waitForReply]);

  const send = useCallback(async (rawText) => {
    const text = String(rawText || "").trim();
    if (!text) return { status: "rejected", reason: "VALIDATION", restoreText: rawText, message: validationCopy([{ field: "text", issue: "EMPTY" }]) };
    if (sendingRef.current || isConversationBusy(stateRef.current)) return { status: "busy" };
    sendingRef.current = true;
    try {
      const clientMessageId = createClientMessageId();
      const startedAt = new Date().toISOString();
      dispatch({ type: "TURN_STARTED", clientMessageId, text, at: startedAt });
      writeOutbox(userId, { clientMessageId, text, startedAt });
      return await deliver(clientMessageId, text);
    } finally {
      sendingRef.current = false;
    }
  }, [deliver, userId]);

  const retry = useCallback(async (clientMessageId) => {
    if (sendingRef.current) return { status: "busy" };
    const text = retryTextFor(stateRef.current, clientMessageId);
    if (!text) return { status: "rejected", reason: "VALIDATION", restoreText: null, message: null };
    sendingRef.current = true;
    try {
      if (!stateRef.current.turns[clientMessageId]) {
        dispatch({ type: "TURN_STARTED", clientMessageId, text, at: new Date().toISOString() });
      }
      writeOutbox(userId, { clientMessageId, text, startedAt: new Date().toISOString() });
      return await deliver(clientMessageId, text);
    } finally {
      sendingRef.current = false;
    }
  }, [deliver, userId]);

  // First page, then whatever this tab or the server says is still open.
  const loadLatest = useCallback(async () => {
    setLoadStatus("loading");
    let page;
    try {
      page = await api.getCoachEvents({ limit: PAGE_SIZE });
    } catch {
      if (aliveRef.current) setLoadStatus("error");
      return;
    }
    if (!aliveRef.current) return;
    dispatch({ type: "EVENTS_LOADED", mode: "latest", events: page.events, nextBefore: page.nextBefore });
    setLoadStatus("ready");

    const pending = pendingReplyRef.current;
    const outbox = readOutbox(userId);
    const findEvent = (id) => page.events.find((item) => item.type === "MESSAGE_USER" && item.clientMessageId === id);
    if (pending?.clientMessageId) {
      const event = findEvent(pending.clientMessageId);
      const text = event?.content || (outbox?.clientMessageId === pending.clientMessageId ? outbox.text : "");
      dispatch({ type: "TURN_STARTED", clientMessageId: pending.clientMessageId, text, at: event?.createdAt || new Date().toISOString() });
      dispatch({ type: "TURN_UPDATED", clientMessageId: pending.clientMessageId, patch: { phase: TURN_PHASES.WAITING } });
      waitForReply(pending.clientMessageId);
    } else if (outbox) {
      const event = findEvent(outbox.clientMessageId);
      if (event) {
        // DONE: answered; FAILED: the server's own bubble offers Retry.
        clearOutbox(userId, outbox.clientMessageId);
      } else {
        dispatch({ type: "TURN_STARTED", clientMessageId: outbox.clientMessageId, text: outbox.text, at: outbox.startedAt || new Date().toISOString() });
        dispatch({ type: "TURN_UPDATED", clientMessageId: outbox.clientMessageId, patch: { phase: TURN_PHASES.UNSENT } });
      }
    }
  }, [api, userId, waitForReply]);

  useEffect(() => {
    if (!enabled) return;
    dispatch({ type: "RESET" });
    loadLatest();
  }, [enabled, loadLatest]);

  const loadOlder = useCallback(async () => {
    const before = stateRef.current.nextBefore;
    if (!before || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const page = await api.getCoachEvents({ before, limit: PAGE_SIZE });
      if (aliveRef.current) dispatch({ type: "EVENTS_LOADED", mode: "older", events: page.events, nextBefore: page.nextBefore });
    } catch (error) {
      // The anchor was erased meanwhile: start again from the newest page.
      if (aliveRef.current && error?.code === "COACH_EVENT_NOT_FOUND") {
        dispatch({ type: "RESET" });
        loadLatest();
      }
    } finally {
      if (aliveRef.current) setLoadingOlder(false);
    }
  }, [api, loadLatest, loadingOlder]);

  const reload = useCallback(() => {
    dispatch({ type: "RESET" });
    return loadLatest();
  }, [loadLatest]);

  // Quietly re-reads the newest page: memory deletions can erase or trim events on the server
  // (coach echo, recall sources), contract §5.5. Events are merged by id; failures are ignored.
  const refreshLatest = useCallback(async () => {
    try {
      const page = await api.getCoachEvents({ limit: PAGE_SIZE });
      if (aliveRef.current) dispatch({ type: "EVENTS_LOADED", mode: "latest", events: page.events, nextBefore: page.nextBefore });
    } catch {
      // The next load shows the server state anyway.
    }
  }, [api]);

  const addEvents = useCallback((events) => {
    dispatch({ type: "EVENTS_ADDED", events });
  }, []);

  // Same shape as an erased event from the server (contract §4.1).
  const markErased = useCallback((eventId) => {
    const event = stateRef.current.events[eventId];
    if (!event) return;
    dispatch({ type: "EVENTS_ADDED", events: [{ ...event, content: null, redacted: true }] });
  }, []);

  const items = useMemo(() => buildThreadItems(state), [state]);

  return {
    items,
    busy: isConversationBusy(state),
    loadStatus,
    hasOlder: Boolean(state.nextBefore),
    loadingOlder,
    send,
    retry,
    loadOlder,
    reload,
    refreshLatest,
    addEvents,
    markErased,
  };
}
